import { getApi } from './api';
import { ILocation, ILocationDetails, ILocationSubscription, ILocationSubscriptionDetail, IPaginationResponse } from '../../model';

const URL = '/location';

const getLocations = async () => {
  const api = await getApi();
  return api.get<ILocation[]>(URL).then((response) => response.data || []);
};

// 404 - the location is not found or is not accessible, the page shows it without an error toast
const getLocation = async (locationId: string) => {
  const api = await getApi({ ignoreErrorStatuses: [404] });
  return api.get<ILocationDetails>(`${URL}/${locationId}`).then((response) => response.data);
};

const getLocationSubscriptions = async (locationId: string, page: number, pageSize: number) => {
  const api = await getApi();
  return api
    .get<IPaginationResponse<ILocationSubscription>>(`${URL}/${locationId}/subscription`, { params: { page, pageSize } })
    .then((response) => response.data);
};

const updateLocationSubscriptionCapacity = async (locationId: string, id: number, capacity: number) => {
  const api = await getApi();
  return api
    .put<ILocationSubscription>(`${URL}/${locationId}/subscription/${id}`, { capacity })
    .then((response) => response.data);
};

// the response contract: JSON has no date type, the API sends UTC dates as ISO strings with 'Z'
type LocationSubscriptionDetailDto = Omit<ILocationSubscriptionDetail, 'startTime' | 'endTime' | 'eventTime'> & {
  startTime?: string | null;
  endTime?: string | null;
  eventTime?: string | null;
};

const toDate = (value?: string | null) => (value ? new Date(value) : null);

// details (events) of the subscription ordered by the event time descending
const getLocationSubscriptionDetails = async (locationId: string, id: number): Promise<ILocationSubscriptionDetail[]> => {
  const api = await getApi();
  return api.get<LocationSubscriptionDetailDto[]>(`${URL}/${locationId}/subscription/${id}/detail`).then((response) =>
    (response.data || []).map((x) => ({
      ...x,
      vehicles: x.vehicles || [],
      startTime: toDate(x.startTime),
      endTime: toDate(x.endTime),
      eventTime: toDate(x.eventTime),
    })),
  );
};

export const locationService = {
  getLocations,
  getLocation,
  getLocationSubscriptions,
  updateLocationSubscriptionCapacity,
  getLocationSubscriptionDetails,
};
